import React from "react";
import { cn } from "../../../../utils/classnames";
import { createTypographyHelper } from "../../../../utils/typography";
import { useBookingEditText } from "../text";
import Select, { components } from "react-select";
import { GoTriangleDown } from "react-icons/go";
import { IoMdCar } from "react-icons/io";

const OptionCard = ({ label, selected, onClick, disabled = false }) => {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={cn("tw:w-full tw:text-left", disabled && "tw:cursor-not-allowed")}
    >
      <div
        className={cn(
          "tw:flex tw:items-center tw:gap-3 tw:h-[56px] tw:rounded-[6px] tw:px-4",
          selected
            ? "tw:border-[1.5px] tw:border-[#282828]"
            : "tw:border tw:border-[#d9d9d9]",
          disabled ? "tw:bg-[#f7f7f7] tw:text-[#A7A7A7]" : "tw:bg-white tw:text-[#282828]"
        )}
      >
        <div
          className={cn(
            "tw:relative tw:h-4 tw:w-4 tw:flex-shrink-0 tw:rounded-full tw:border-[1.5px]",
            selected ? "tw:border-[#282828]" : "tw:border-[#d9d9d9]"
          )}
        >
          {selected && (
            <div className="tw:absolute tw:left-1/2 tw:top-1/2 tw:h-[7px] tw:w-[7px] tw:-translate-x-1/2 tw:-translate-y-1/2 tw:rounded-full tw:bg-[#282828]" />
          )}
        </div>
        <span className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400]">{label}</span>
      </div>
    </button>
  );
};

const BasePriceDisabledCard = ({ leftLabel, rightLabel, selected = false }) => {
  return (
    <div
      className={cn(
        "tw:flex tw:items-center tw:justify-between tw:h-[56px] tw:rounded-[6px] tw:px-4 tw:text-[#A7A7A7]",
        selected ? "tw:border-[1.5px] tw:border-[#282828] tw:bg-[#f7f7f7]" : "tw:border tw:border-[#efefef] tw:bg-[#f7f7f7]"
      )}
    >
      <div className="tw:flex tw:items-center tw:gap-3">
        <div
          className={cn(
            "tw:relative tw:h-4 tw:w-4 tw:flex-shrink-0 tw:rounded-full tw:border-[1.5px]",
            selected ? "tw:border-[#282828]" : "tw:border-[#d9d9d9]"
          )}
        >
          {selected && (
            <div className="tw:absolute tw:left-1/2 tw:top-1/2 tw:h-[7px] tw:w-[7px] tw:-translate-x-1/2 tw:-translate-y-1/2 tw:rounded-full tw:bg-[#282828]" />
          )}
        </div>
        <span className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400]">{leftLabel}</span>
      </div>
      <span className="tw:font-hanken-grotesk tw:text-[15px] tw:font-[400]">{rightLabel}</span>
    </div>
  );
};

const SettingsSection = ({
  basePriceType,
  setBasePriceType,
  garageType,
  setGarageType,
  discountType,
  setDiscountType,
  discountValue,
  setDiscountValue,
  departureGarageId,
  setDepartureGarageId,
  returnGarageId,
  setReturnGarageId,
  garageOptions = [],
  canUseCustomGarage = false,
}) => {
  const { t, language } = useBookingEditText();
  const getTypographyClass = createTypographyHelper(language);
  const selectedDepartureGarage = garageOptions.find((option) => option.value === departureGarageId) || null;
  const selectedArrivalGarage = garageOptions.find((option) => option.value === returnGarageId) || null;
  const menuPortalTarget = typeof window !== "undefined" ? document.body : null;

  const garageSelectStyles = {
    control: (baseStyles) => ({
      ...baseStyles,
      height: "50px",
      borderRadius: "5px",
      border: "1px solid #e8e8e8",
      backgroundColor: "#ffffff",
      fontSize: "14px",
      boxShadow: "none",
      "&:hover": {
        border: "1px solid #e8e8e8",
      },
    }),
    valueContainer: (baseStyles) => ({
      ...baseStyles,
      paddingLeft: "40px",
    }),
    menu: (baseStyles) => ({
      ...baseStyles,
      borderRadius: "5px",
      marginTop: "4px",
      zIndex: 40,
    }),
    menuPortal: (baseStyles) => ({
      ...baseStyles,
      zIndex: 40,
    }),
    option: (baseStyles, state) => ({
      ...baseStyles,
      fontSize: "14px",
      backgroundColor: state.isSelected ? "#dbbe78" : state.isFocused ? "#eddfbb" : "#ffffff",
      color: state.isSelected ? "#ffffff" : "#282828",
      cursor: "pointer",
    }),
  };

  return (
    <section>
      <div className="tw:!px-5 tw:!pt-5 tw:!pb-2">
        <div className="tw:!mb-0 tw:!text-[#282828] tw:!text-[16px] tw:!font-[600] tw:font-hanken-grotesk">
          {t("editBooking.BookingEditPage.settings.title")}
        </div>
      </div>
      <div className="tw:flex tw:flex-col tw:gap-5 tw:pt-5 tw:px-5">
        <div className="tw:flex tw:flex-col tw:gap-2">
          <div className={cn("tw:text-[#8d8d8d]", getTypographyClass(13))}>
            {t("editBooking.BookingEditPage.settings.basePrice")}
          </div>
          <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-4 tw:gap-2">
            <div className="tw:md:col-span-3 tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-2">
              <OptionCard
                label={t("editBooking.BookingEditPage.settings.basePriceOptions.tlfPrice")}
                selected={basePriceType === "tlfPrice"}
                onClick={() => setBasePriceType("tlfPrice")}
              />
              <BasePriceDisabledCard
                leftLabel={t("editBooking.BookingEditPage.settings.basePriceOptions.outOfTokyoPrice")}
                rightLabel={t("editBooking.BookingEditPage.settings.notApplicable")}
                selected={basePriceType === "outsideTokyo"}
              />
            </div>
          </div>
        </div>

        <div className="tw:flex tw:flex-col tw:gap-2">
          <div className={cn("tw:text-[#8d8d8d]", getTypographyClass(13))}>
            {t("editBooking.BookingEditPage.settings.dispatchArrivalGarage")}
          </div>
          <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-4 tw:gap-2">
            <div className="tw:md:col-span-3 tw:flex tw:flex-col tw:gap-0">
              <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-2">
                <OptionCard
                  label={t("editBooking.BookingEditPage.settings.dispatchArrivalGarageOptions.default")}
                  selected={garageType === "default"}
                  onClick={() => setGarageType("default")}
                />

                <OptionCard
                  label={t("editBooking.BookingEditPage.settings.dispatchArrivalGarageOptions.custom")}
                  selected={garageType === "custom"}
                  onClick={() => setGarageType("custom")}
                />
              </div>

              {garageType === "default" && (
                <div className="tw:mt-3 tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-2">
                  <div className="tw:rounded-[5px] tw:border tw:border-[#E8E8E8] tw:bg-[#F9F9F9] tw:px-4 tw:py-3 tw:flex tw:flex-col tw:gap-3">
                    <div className="tw:flex tw:items-center tw:justify-between">
                      <div className="tw:text-[#8D8D8D] tw:text-[14px] tw:leading-[20px]">
                        {t("editBooking.BookingEditPage.settings.dispatchGarage")}
                      </div>
                      <div className="tw:text-[#282828] tw:text-[15px] tw:leading-[22px]">
                        Tokyo
                      </div>
                    </div>

                    <div className="tw:flex tw:items-center tw:justify-between">
                      <div className="tw:text-[#8D8D8D] tw:text-[14px] tw:leading-[20px]">
                        {t("editBooking.BookingEditPage.settings.arrivalGarage")}
                      </div>
                      <div className="tw:text-[#282828] tw:text-[15px] tw:leading-[22px]">
                        Tokyo
                      </div>
                    </div>
                  </div>
                  <div />
                </div>
              )}

              {garageType === "custom" && (
                <div className="tw:mt-3 tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-2">
                  <div />
                  <div className="tw:flex tw:flex-col tw:gap-3">
                    <div className="tw:flex tw:flex-col tw:lg:flex-row tw:lg:items-center">
                      <div className="tw:w-[115px] tw:flex-shrink-0 tw:text-[#8D8D8D] tw:text-[14px] tw:leading-[20px]">
                        {t("editBooking.BookingEditPage.settings.dispatchGarage")}
                      </div>
                      <div className="tw:w-full tw:lg:ml-6 tw:relative">
                        <span className="tw:absolute tw:left-[14px] tw:top-1/2 tw:z-[2] tw:-translate-y-1/2 tw:text-[#DBBE78]">
                          <IoMdCar size={18} />
                        </span>
                        <Select
                          placeholder={t("editBooking.BookingEditPage.settings.dispatchGarage")}
                          classNamePrefix="react-select-settings-dispatch"
                          styles={garageSelectStyles}
                          autoComplete="off"
                          menuPortalTarget={menuPortalTarget}
                          menuPosition="fixed"
                          value={selectedDepartureGarage}
                          options={garageOptions}
                          onChange={(option) => setDepartureGarageId(option?.value || "")}
                          components={{
                            DropdownIndicator: (props) => (
                              <components.DropdownIndicator {...props}>
                                <div className="tw:pr-2">
                                  <GoTriangleDown color="#DBBE78" size={16} />
                                </div>
                              </components.DropdownIndicator>
                            ),
                            IndicatorSeparator: () => null,
                          }}
                        />
                      </div>
                    </div>

                    <div className="tw:flex tw:flex-col tw:lg:flex-row tw:lg:items-center">
                      <div className="tw:w-[115px] tw:flex-shrink-0 tw:text-[#8D8D8D] tw:text-[14px] tw:leading-[20px]">
                        {t("editBooking.BookingEditPage.settings.arrivalGarage")}
                      </div>
                      <div className="tw:w-full tw:lg:ml-6 tw:relative">
                        <span className="tw:absolute tw:left-[14px] tw:top-1/2 tw:z-[2] tw:-translate-y-1/2 tw:text-[#DBBE78]">
                          <IoMdCar size={18} />
                        </span>
                        <Select
                          placeholder={t("editBooking.BookingEditPage.settings.arrivalGarage")}
                          classNamePrefix="react-select-settings-arrival"
                          styles={garageSelectStyles}
                          autoComplete="off"
                          menuPortalTarget={menuPortalTarget}
                          menuPosition="fixed"
                          value={selectedArrivalGarage}
                          options={garageOptions}
                          onChange={(option) => setReturnGarageId(option?.value || "")}
                          components={{
                            DropdownIndicator: (props) => (
                              <components.DropdownIndicator {...props}>
                                <div className="tw:pr-2">
                                  <GoTriangleDown color="#DBBE78" size={16} />
                                </div>
                              </components.DropdownIndicator>
                            ),
                            IndicatorSeparator: () => null,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="tw:flex tw:flex-col tw:gap-2">
          <div className={cn("tw:text-[#8d8d8d]", getTypographyClass(13))}>
            {t("editBooking.BookingEditPage.settings.discount")}
          </div>
          <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-4 tw:gap-2">
            <div className="tw:md:col-span-3 tw:flex tw:flex-col tw:gap-0">
              <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-2">
                <OptionCard
                  label={t("editBooking.BookingEditPage.settings.discountOptions.notApplicable")}
                  selected={discountType === "notApplicable"}
                  onClick={() => setDiscountType("notApplicable")}
                />
                <OptionCard
                  label={t("editBooking.BookingEditPage.settings.discountOptions.applicable")}
                  selected={discountType === "applicable"}
                  onClick={() => setDiscountType("applicable")}
                />
              </div>

              {discountType === "applicable" && (
                <div className="tw:mt-3 tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-2">
                  <div />
                  <div className="tw:flex tw:flex-col tw:lg:flex-row tw:lg:items-center">
                    <div className="tw:w-[115px] tw:flex-shrink-0 tw:text-[#8D8D8D] tw:text-[14px] tw:leading-[20px]">
                      Discount Rate
                    </div>
                    <div className="tw:w-full tw:lg:ml-6 tw:relative">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={discountValue}
                        onChange={(e) => {
                          const nextValue = Number(e.target.value || 0);
                          const clampedValue = Math.max(0, Math.min(100, nextValue));
                          setDiscountValue(clampedValue);
                        }}
                        className="tw:h-[50px] tw:w-full tw:rounded-[5px] tw:border tw:border-[#e8e8e8] tw:bg-white tw:px-[14px] tw:pr-10 tw:text-[15px] tw:font-normal tw:leading-[22px] tw:text-[#282828] tw:outline-none focus:tw:border-[#282828]"
                      />
                      <span className="tw:absolute tw:right-[14px] tw:top-1/2 tw:-translate-y-1/2 tw:text-[14px] tw:font-normal tw:leading-[20px] tw:text-[#282828]">
                        %
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SettingsSection;
